import React, { useState, useEffect, useCallback, useRef } from "react";
import useApiStable from "../../utils/useApiStable";
import { useAuth } from "../../context/AuthContext";
import BockModal from "./BockModal";

const formatTime = (t) => (t ? t.substring(0, 5) : "");

const DURATION_OPTIONS = [30, 60, 90, 120];

const BockRespondModal = ({ bock, onConfirm, onClose }) => {
    const defaultStart = bock.timeFrom ? formatTime(bock.timeFrom) : "15:00";
    const [startTime, setStartTime] = useState(defaultStart);
    const [duration, setDuration] = useState(60);

    return (
        <div
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: "#fff", borderRadius: 8, padding: 24,
                    width: "90%", maxWidth: 360, boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
                }}
                onClick={e => e.stopPropagation()}
            >
                <h3 style={{ marginTop: 0, marginBottom: 4 }}>Spielen mit {bock.personName}?</h3>
                <p style={{ color: "#666", fontSize: 13, marginBottom: 20 }}>
                    Verfügbar {formatTime(bock.timeFrom)}–{formatTime(bock.timeTo)} Uhr
                </p>

                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Wann?</label>
                    <input
                        type="time"
                        value={startTime}
                        min={formatTime(bock.timeFrom)}
                        max={formatTime(bock.timeTo)}
                        onChange={e => setStartTime(e.target.value)}
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid #ddd", borderRadius: 4, boxSizing: "border-box" }}
                    />
                </div>

                <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 8 }}>Wie lange?</label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {DURATION_OPTIONS.map(d => (
                            <button
                                key={d}
                                onClick={() => setDuration(d)}
                                style={{
                                    padding: "6px 14px", borderRadius: 4, cursor: "pointer", fontSize: 13,
                                    border: "1px solid #4CAF50",
                                    background: duration === d ? "#4CAF50" : "#fff",
                                    color: duration === d ? "#fff" : "#4CAF50",
                                }}
                            >
                                {d} Min.
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                        onClick={onClose}
                        style={{ padding: "8px 16px", border: "1px solid #ddd", background: "#fff", borderRadius: 4, cursor: "pointer" }}
                    >
                        Abbrechen
                    </button>
                    <button
                        onClick={() => onConfirm(startTime, duration)}
                        style={{ padding: "8px 16px", border: "none", background: "#4CAF50", color: "#fff", borderRadius: 4, cursor: "pointer", fontWeight: "bold" }}
                    >
                        Anfrage senden
                    </button>
                </div>
            </div>
        </div>
    );
};

const BockToast = ({ toasts, onDismiss }) => {
    if (toasts.length === 0) return null;
    return (
        <div style={{
            position: "fixed", top: 16, right: 16, zIndex: 2000,
            display: "flex", flexDirection: "column", gap: 8,
            maxWidth: 320,
        }}>
            {toasts.map(t => (
                <div key={t.id} style={{
                    background: "#2e7d32", color: "#fff",
                    padding: "12px 16px", borderRadius: 8,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    fontSize: 14, lineHeight: 1.4,
                    display: "flex", alignItems: "flex-start", gap: 10,
                    animation: "slideIn 0.2s ease",
                }}>
                    <span style={{ flex: 1 }}>{t.message}</span>
                    <button
                        onClick={() => onDismiss(t.id)}
                        style={{
                            background: "none", border: "none", color: "#fff",
                            cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1,
                            opacity: 0.7, flexShrink: 0,
                        }}
                    >×</button>
                </div>
            ))}
        </div>
    );
};

const BockView = () => {
    const api = useApiStable();
    const { user } = useAuth();
    const [bocks, setBocks] = useState([]);
    const [confirmations, setConfirmations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [respondTarget, setRespondTarget] = useState(null);
    const [error, setError] = useState(null);
    const [toasts, setToasts] = useState([]);
    const knownIdsRef = useRef(null);
    const toastCounterRef = useRef(0);

    const myBock = bocks.find(b => b.personUuid === user?.personUuid);

    const showToast = useCallback((message) => {
        const id = ++toastCounterRef.current;
        setToasts(prev => [...prev, { id, message }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const load = useCallback(async (isPolling = false) => {
        try {
            const [data, confs] = await Promise.all([
                api.get("/bock/today"),
                api.get("/bock/my-confirmations"),
            ]);

            if (isPolling && knownIdsRef.current !== null) {
                const newEntries = data.filter(b => !knownIdsRef.current.has(b.id) && b.personUuid !== user?.personUuid);
                if (newEntries.length > 0) {
                    newEntries.forEach(b => {
                        const msg = `${b.personName} hat Bock! (${formatTime(b.timeFrom)}–${formatTime(b.timeTo)})`;
                        showToast(msg);
                        notify(msg);
                    });
                }
            }

            knownIdsRef.current = new Set(data.map(b => b.id));
            setBocks(data);
            setConfirmations(Array.isArray(confs) ? confs : []);
        } catch {
            // silently ignore polling errors
        } finally {
            setLoading(false);
        }
    }, [api, user?.personUuid, showToast]);

    useEffect(() => {
        load();
        const interval = setInterval(() => load(true), 15000);
        const onVisible = () => { if (!document.hidden) load(true); };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [load]);

    const notify = (message) => {
        if (!("Notification" in window)) return;
        if (Notification.permission === "granted") {
            new Notification("Bock-List", { body: message, icon: "/favicon.ico" });
        }
    };

    const requestNotificationPermission = () => {
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    };

    const handleSetBock = async (timeFrom, timeTo) => {
        setShowModal(false);
        setError(null);
        try {
            await api.post("/bock", { timeFrom, timeTo });
            requestNotificationPermission();
            await load();
        } catch (err) {
            setError("Fehler beim Setzen des Bocks.");
        }
    };

    const handleRemoveBock = async () => {
        if (!myBock) return;
        setError(null);
        try {
            await api.delete(`/bock/${myBock.id}`);
            await load();
        } catch {
            setError("Fehler beim Entfernen des Bocks.");
        }
    };

    const handleRespond = async (startTime, duration) => {
        if (!respondTarget) return;
        setError(null);
        try {
            await api.post(`/bock/${respondTarget.id}/respond`, {
                proposedStartTime: startTime,
                durationMinutes: duration,
            });
            setRespondTarget(null);
            await load();
        } catch {
            setError("Fehler beim Senden der Anfrage.");
            setRespondTarget(null);
        }
    };

    const handleAccept = async (confirmationId) => {
        setError(null);
        try {
            await api.post(`/bock/confirmation/${confirmationId}/accept`);
            await load();
        } catch {
            setError("Fehler beim Annehmen der Anfrage.");
        }
    };

    const handleDecline = async (confirmationId) => {
        setError(null);
        try {
            await api.post(`/bock/confirmation/${confirmationId}/decline`);
            await load();
        } catch {
            setError("Fehler beim Ablehnen der Anfrage.");
        }
    };

    const handleWithdrawRequest = async (confirmationId) => {
        setError(null);
        try {
            await api.delete(`/bock/confirmation/${confirmationId}`);
            await load();
        } catch {
            setError("Fehler beim Zurückziehen der Anfrage.");
        }
    };

    const handleCancelMatch = async (bockId) => {
        setError(null);
        try {
            await api.delete(`/bock/${bockId}`);
            await load();
        } catch {
            setError("Fehler beim Absagen des Matches.");
        }
    };

    if (loading) return <div className="groupphase-section"><p style={{ color: "#888" }}>Lade...</p></div>;

    const othersWithBock = bocks.filter(b => b.personUuid !== user?.personUuid);

    return (
        <div className="groupphase-section">
            <BockToast toasts={toasts} onDismiss={dismissToast} />
            {showModal && (
                <BockModal
                    onConfirm={handleSetBock}
                    onClose={() => setShowModal(false)}
                />
            )}
            {respondTarget && (
                <BockRespondModal
                    bock={respondTarget}
                    onConfirm={handleRespond}
                    onClose={() => setRespondTarget(null)}
                />
            )}

            <h2 style={{ marginBottom: 4 }}>Bock-List</h2>
            <p style={{ color: "#666", fontSize: 14, marginBottom: 20 }}>
                Hast du heute spontan Lust zu spielen? Sag anderen Bescheid!
            </p>

            {error && <p style={{ color: "#e65100", marginBottom: 12 }}>{error}</p>}

            {/* My bock status */}
            <div style={{ marginBottom: 24 }}>
                {myBock ? (
                    <div style={{
                        padding: "12px 16px",
                        background: myBock.accepted ? "#e3f2fd" : "#e8f5e9",
                        border: `1px solid ${myBock.accepted ? "#1976d2" : "#4CAF50"}`,
                        borderRadius: 6,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}>
                        <div>
                            {myBock.accepted ? (
                                <>
                                    <div style={{ fontWeight: "bold", color: "#1565c0" }}>Match bestätigt!</div>
                                    <div style={{ fontSize: 13, color: "#555" }}>
                                        mit {myBock.matchPartnerName} · {formatTime(myBock.matchTime)} Uhr · {myBock.matchDurationMinutes} Min.
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{ fontWeight: "bold", color: "#2e7d32" }}>Du hast Bock!</div>
                                    <div style={{ fontSize: 13, color: "#555" }}>
                                        {formatTime(myBock.timeFrom)} – {formatTime(myBock.timeTo)} Uhr
                                    </div>
                                </>
                            )}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            {!myBock.accepted && (
                                <button
                                    onClick={() => setShowModal(true)}
                                    style={{
                                        padding: "6px 12px", fontSize: 13, cursor: "pointer",
                                        border: "1px solid #4CAF50", background: "#fff",
                                        color: "#4CAF50", borderRadius: 4,
                                    }}
                                >
                                    Ändern
                                </button>
                            )}
                            <button
                                onClick={handleRemoveBock}
                                style={{
                                    padding: "6px 12px", fontSize: 13, cursor: "pointer",
                                    border: "1px solid #e65100", background: "#fff",
                                    color: "#e65100", borderRadius: 4,
                                }}
                            >
                                Absagen
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowModal(true)}
                        style={{
                            padding: "12px 28px",
                            fontSize: 18,
                            fontWeight: "bold",
                            background: "#4CAF50",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
                        }}
                    >
                        🎾 Ich hab Bock!
                    </button>
                )}
            </div>

            {/* Incoming confirmation requests (I am the bock owner) */}
            {confirmations.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 16, marginBottom: 12, color: "#e65100" }}>
                        Eingehende Anfragen ({confirmations.length})
                    </h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {confirmations.map(c => (
                            <div
                                key={c.id}
                                style={{
                                    padding: "12px 16px",
                                    border: "1.5px solid #ff8f00",
                                    borderRadius: 8,
                                    background: "#fff8e1",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 12,
                                    flexWrap: "wrap",
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: "bold", fontSize: 14 }}>{c.requesterName}</div>
                                    <div style={{ fontSize: 13, color: "#555", marginTop: 2 }}>
                                        {formatTime(c.proposedStartTime)} Uhr · {c.durationMinutes} Min.
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <button
                                        onClick={() => handleAccept(c.id)}
                                        style={{
                                            padding: "6px 14px", fontSize: 13, cursor: "pointer",
                                            border: "none", background: "#4CAF50",
                                            color: "#fff", borderRadius: 4, fontWeight: "bold",
                                        }}
                                    >
                                        Annehmen
                                    </button>
                                    <button
                                        onClick={() => handleDecline(c.id)}
                                        style={{
                                            padding: "6px 14px", fontSize: 13, cursor: "pointer",
                                            border: "1px solid #e65100", background: "#fff",
                                            color: "#e65100", borderRadius: 4,
                                        }}
                                    >
                                        Ablehnen
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Others with bock */}
            <h3 style={{ fontSize: 16, marginBottom: 12 }}>
                Heute dabei ({othersWithBock.length})
            </h3>

            {othersWithBock.length === 0 ? (
                <p style={{ color: "#888", fontStyle: "italic" }}>
                    Noch niemand anderes hat heute Bock gemeldet.
                </p>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {othersWithBock.map(b => (
                        <div
                            key={b.id}
                            style={{
                                padding: "10px 14px",
                                border: `1px solid ${b.accepted ? "#1976d2" : "#ddd"}`,
                                borderRadius: 6,
                                background: b.accepted ? "#e3f2fd" : "#fafafa",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <div>
                                <div style={{ fontWeight: "bold", fontSize: 14 }}>{b.personName}</div>
                                {b.accepted ? (
                                    <div style={{ fontSize: 13, color: "#1565c0" }}>
                                        Match bestätigt · {formatTime(b.matchTime)} Uhr · {b.matchDurationMinutes} Min.
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 13, color: "#555" }}>
                                        {formatTime(b.timeFrom)} – {formatTime(b.timeTo)} Uhr
                                    </div>
                                )}
                            </div>
                            {b.accepted ? (
                                <button
                                    onClick={() => handleCancelMatch(b.id)}
                                    style={{
                                        padding: "6px 12px", fontSize: 13, cursor: "pointer",
                                        border: "1px solid #e65100", background: "#fff",
                                        color: "#e65100", borderRadius: 4, whiteSpace: "nowrap",
                                    }}
                                >
                                    Absagen
                                </button>
                            ) : b.myPendingConfirmationId ? (
                                <button
                                    onClick={() => handleWithdrawRequest(b.myPendingConfirmationId)}
                                    style={{
                                        padding: "6px 12px", fontSize: 13, cursor: "pointer",
                                        border: "1px solid #ff8f00", background: "#fff8e1",
                                        color: "#e65100", borderRadius: 4, whiteSpace: "nowrap",
                                    }}
                                >
                                    Anfrage zurückziehen
                                </button>
                            ) : (
                                <button
                                    onClick={() => setRespondTarget(b)}
                                    style={{
                                        padding: "6px 14px", fontSize: 13, cursor: "pointer",
                                        border: "1px solid #4CAF50", background: "#fff",
                                        color: "#4CAF50", borderRadius: 4, whiteSpace: "nowrap",
                                    }}
                                >
                                    Spielen?
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BockView;
